/**
 * Hero — the ad-traffic first impression. Renders without images (fast LCP):
 * the product visual is a live React replica of the rota screen.
 */
import { Link } from 'react-router-dom';
import { CalendarCheck2, PoundSterling, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RotaReplica } from '@/components/landing/ProductReplicas';
import { marketingEvent } from '@/lib/marketing';

const TRUST_POINTS = ['14-day free trial', 'No setup fee', 'No per-employee pricing'];

export function HeroSection({ onBookDemo }) {
  return (
    <section id="top" className="relative overflow-hidden scroll-mt-24">
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full hero-decor-gradient blur-3xl opacity-70" aria-hidden="true" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-14 sm:pt-20 sm:pb-20 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center">
        <div>
          <p className="pill bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)]" data-testid="marketing-hero-eyebrow">
            Restaurant workforce management
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-[3.4rem] font-display font-semibold tracking-tight leading-[1.06]" data-testid="marketing-hero-heading">
            Run Your Restaurant.
            <br />
            <span className="text-[color:var(--brand-primary)]">Not Your Rota.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[color:var(--brand-text-muted)] max-w-xl leading-relaxed" data-testid="marketing-hero-subcopy">
            RotaHQ brings staff scheduling, attendance tracking and payroll together in one
            simple platform built for restaurants.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button asChild className="h-12 rounded-xl px-6 text-base bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white font-semibold transition-colors duration-150 active:scale-[0.99]">
              <Link to="/signup" onClick={() => marketingEvent('click_start_trial', { placement: 'hero' })} data-testid="marketing-hero-start-trial-button">
                Start Your Free Trial
              </Link>
            </Button>
            <Button
              variant="outline"
              className="h-12 rounded-xl px-6 text-base bg-white border-[color:var(--brand-border)] font-semibold hover:bg-[color:var(--brand-bg-2)] transition-colors duration-150"
              onClick={() => onBookDemo('hero')}
              data-testid="marketing-hero-book-demo-button"
            >
              Book a Demo
            </Button>
          </div>
          <ul className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[color:var(--brand-text-muted)]" data-testid="marketing-hero-trust-points">
            {TRUST_POINTS.map((point, i) => (
              <li key={point} className="flex items-center gap-1.5">
                {i === 0 && <Timer className="h-4 w-4 text-[color:var(--brand-primary)]" aria-hidden="true" />}
                {i === 1 && <CalendarCheck2 className="h-4 w-4 text-[color:var(--brand-primary)]" aria-hidden="true" />}
                {i === 2 && <PoundSterling className="h-4 w-4 text-[color:var(--brand-primary)]" aria-hidden="true" />}
                {point}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <RotaReplica className="shadow-[0_24px_60px_rgba(31,35,40,0.12)]" />
        </div>
      </div>
    </section>
  );
}
